import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createVNode as _createVNode, withCtx as _withCtx, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  key: 1,
  class: "flex flex-col gap-y-4"
}

import { ref, reactive, computed, onMounted } from 'vue'
import { useToast } from 'vue-toastification'
import Network from '@/Network'
import { useErrorToast } from '@/composables/useErrorToast'
import { ScheduleResponse } from '@/Network/Types/Responses/ScheduleUnitResponses'
import Spinner from '@/components/others/Spinner.vue'
import DropdownCard from '@/components/designSystem/Cards/DropdownCard.vue'
import TeamExchangeConfigBlock from './TeamExchangeConfigBlock.vue'
import {
  createDefaultTeamHandoverConfig,
  isTeamHandoverConfigValid,
  buildTeamHandoverPayload,
  TEAM_HANDOVER_VALIDATION_RULES,
  TEAM_CONSECUTIVE_HOURS_VALIDATION_RULES,
  type TeamHandoverConfig,
  type HandoverTiming,
  type HandoverReference
} from '@/constants/handover.constants'

type TeamHandoverSource = {
  allow?: boolean
  approval?: boolean
  requestHours?: number | null
  requestTiming?: string | null
  requestReference?: string | null
  expirationHours?: number | null
  expirationTiming?: string | null
  expirationReference?: string | null
  limitConsecutiveHours?: boolean
  maxConsecutiveHours?: number | null
  minRestHours?: number | null
  consecutiveHoursOurTeamOnly?: boolean
}

// Extraído para reduzir a complexidade ciclomática de hydrateTeam (regra eslint `complexity`).

export default /*#__PURE__*/_defineComponent({
  __name: 'ExchangeConfigTab',
  props: {
    scheduleId: {}
  },
  emits: ["update:isSaving"],
  setup(__props: any, { expose: __expose, emit }) {

const props = __props;




const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const originalSnapshot = ref('')
const errorsOurTeam = reactive<Record<string, string>>({})
const errorsNotOurTeam = reactive<Record<string, string>>({})

const local = reactive<{ ourTeam: TeamHandoverConfig; notOurTeam: TeamHandoverConfig }>({
  ourTeam: createDefaultTeamHandoverConfig(),
  notOurTeam: createDefaultTeamHandoverConfig()
})

const clearOurTeamError = (field: string) => delete errorsOurTeam[field]
const clearNotOurTeamError = (field: string) => delete errorsNotOurTeam[field]

function validateTeam(config: TeamHandoverConfig, errs: Record<string, string>): boolean {
  Object.keys(errs).forEach((k) => delete errs[k])

  // Jornada é validada sempre — independe da restrição de trocas.
  if (config.limitConsecutiveHours) {
    for (const [field, message] of Object.entries(TEAM_CONSECUTIVE_HOURS_VALIDATION_RULES)) {
      if (!config[field as keyof TeamHandoverConfig]) errs[field] = message
    }
  }

  if (config.allowDoctorStationHandover) {
    for (const [field, message] of Object.entries(TEAM_HANDOVER_VALIDATION_RULES)) {
      if (!config[field as keyof TeamHandoverConfig]) errs[field] = message
    }
  }

  return Object.keys(errs).length === 0
}

function validate(): boolean {
  const okOur = validateTeam(local.ourTeam, errorsOurTeam)
  const okNot = validateTeam(local.notOurTeam, errorsNotOurTeam)
  return okOur && okNot
}

function getLocalSnapshot() {
  return JSON.stringify(buildTeamHandoverPayload(local.ourTeam, local.notOurTeam))
}

const hasUnsavedChanges = computed(() => {
  if (!originalSnapshot.value) return false
  return getLocalSnapshot() !== originalSnapshot.value
})

const isFormValid = computed(
  () => isTeamHandoverConfigValid(local.ourTeam) && isTeamHandoverConfigValid(local.notOurTeam)
)

// Fonte crua vinda do ScheduleResponse (chaves já resolvidas por time pelo chamador).
function toNullableString(value: number | null | undefined): string | null {
  return value != null ? String(value) : null
}

function hydrateTeam(config: TeamHandoverConfig, source: TeamHandoverSource) {
  config.allowDoctorStationHandover = source.allow ?? false
  config.requiresAdminApproval = source.approval ?? true
  config.handoverRequestHours = toNullableString(source.requestHours)
  config.handoverRequestTiming = (source.requestTiming as HandoverTiming) ?? null
  config.handoverRequestReference = (source.requestReference as HandoverReference) ?? null
  config.handoverExpirationHours = toNullableString(source.expirationHours)
  config.handoverExpirationTiming = (source.expirationTiming as HandoverTiming) ?? null
  config.handoverExpirationReference = (source.expirationReference as HandoverReference) ?? null
  config.limitConsecutiveHours = source.limitConsecutiveHours ?? false
  config.maxConsecutiveHours = toNullableString(source.maxConsecutiveHours)
  config.minRestHours = toNullableString(source.minRestHours)
  config.consecutiveHoursOurTeamOnly = source.consecutiveHoursOurTeamOnly ?? false
}

const load = async () => {
  isLoading.value = true
  try {
    const schedule = (await Network.schedule.getById(props.scheduleId)) as ScheduleResponse

    hydrateTeam(local.ourTeam, {
      allow: schedule.allowDoctorStationHandover,
      approval: schedule.requiresAdminApproval,
      requestHours: schedule.handoverRequestHours,
      requestTiming: schedule.handoverRequestTiming,
      requestReference: schedule.handoverRequestReference,
      expirationHours: schedule.handoverExpirationHours,
      expirationTiming: schedule.handoverExpirationTiming,
      expirationReference: schedule.handoverExpirationReference,
      limitConsecutiveHours: schedule.limitConsecutiveHours,
      maxConsecutiveHours: schedule.maxConsecutiveHours,
      minRestHours: schedule.minRestHours,
      consecutiveHoursOurTeamOnly: schedule.consecutiveHoursOurTeamOnly
    })
    hydrateTeam(local.notOurTeam, {
      allow: schedule.allowDoctorStationHandoverNotOurTeam,
      approval: schedule.requiresAdminApprovalNotOurTeam,
      requestHours: schedule.handoverRequestHoursNotOurTeam,
      requestTiming: schedule.handoverRequestTimingNotOurTeam,
      requestReference: schedule.handoverRequestReferenceNotOurTeam,
      expirationHours: schedule.handoverExpirationHoursNotOurTeam,
      expirationTiming: schedule.handoverExpirationTimingNotOurTeam,
      expirationReference: schedule.handoverExpirationReferenceNotOurTeam,
      limitConsecutiveHours: schedule.limitConsecutiveHoursNotOurTeam,
      maxConsecutiveHours: schedule.maxConsecutiveHoursNotOurTeam,
      minRestHours: schedule.minRestHoursNotOurTeam,
      consecutiveHoursOurTeamOnly: schedule.consecutiveHoursOurTeamOnlyNotOurTeam
    })

    originalSnapshot.value = getLocalSnapshot()
  } catch (err) {
    notifyError(err, 'Não foi possível carregar as configurações de trocas e jornada.')
  } finally {
    isLoading.value = false
  }
}

onMounted(load)

const handleSave = async () => {
  if (!validate()) return
  emit('update:isSaving', true)
  try {
    await Network.schedule.update(
      props.scheduleId,
      buildTeamHandoverPayload(local.ourTeam, local.notOurTeam)
    )
    originalSnapshot.value = getLocalSnapshot()
    toast.success('Configurações de trocas e jornada salvas com sucesso.')
  } catch (err: unknown) {
    // `Network.schedule.update` normaliza o erro em ServiceError, então
    // `axios.isAxiosError` deixou de casar e a mensagem do backend se perdia.
    notifyError(err, 'Não foi possível salvar as configurações de trocas e jornada.')
  } finally {
    emit('update:isSaving', false)
  }
}

__expose({
  handleSave,
  hasUnsavedChanges,
  isFormValid,
  // exposto para teste unitário do estado local (mesma referência mutada pelos blocos filhos)
  local
})

return (_ctx: any,_cache: any) => {
  return (isLoading.value)
    ? (_openBlock(), _createBlock(Spinner, {
        key: 0,
        color: "primary",
        size: "5rem",
        width: "0.5rem",
        class: "absolute inset-0 flex justify-center items-center bg-white bg-opacity-50 z-50"
      }))
    : (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(DropdownCard, {
          title: "Nossa Equipe (Time Interno)",
          subtitle: "Médicos do seu time, contratados e gerenciados pela sua empresa.",
          "start-open": false,
          "test-id": "our-team-accordion-toggle",
          class: "border border-lightGray30"
        }, {
          default: _withCtx(() => [
            _createVNode(TeamExchangeConfigBlock, {
              config: local.ourTeam,
              errors: errorsOurTeam,
              "on-clear-error": clearOurTeamError,
              "testid-prefix": "our-team"
            }, null, 8, ["config", "errors"])
          ]),
          _: 1
        }),
        _createVNode(DropdownCard, {
          title: "Equipe da Unidade (Time Externo)",
          subtitle: "Médicos externos, vinculados à própria unidade de saúde.",
          "start-open": true,
          "test-id": "not-our-team-accordion-toggle",
          class: "border border-lightGray30"
        }, {
          default: _withCtx(() => [
            _createVNode(TeamExchangeConfigBlock, {
              config: local.notOurTeam,
              errors: errorsNotOurTeam,
              "on-clear-error": clearNotOurTeamError,
              "testid-prefix": "not-our-team"
            }, null, 8, ["config", "errors"])
          ]),
          _: 1
        })
      ]))
}
}

})